import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  consultasService, 
  pacientesService, 
  prontuariosService,
  catalogoService
} from '../services/api';
import { medicamentosService } from '../services/medicamentosService';
import type { Consulta } from '../types';
import type { Paciente } from '../services/api/pacientesService';

import DadosNaoCarregados from '../components/DadosNaoCarregados';
import OCRUploadModal from '../components/OCRUploadModal';

interface CatalogoItem {
  id: number;
  valor: string;
}

interface MedicamentoSelecionado {
  medicamento_id: number;
  dosagem: string;
  posologia: string;
  observacao: string;
}

export default function ProntuarioConsulta() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [consulta, setConsulta] = useState<Consulta | null>(null);
  const [paciente, setPaciente] = useState<Paciente | null>(null);
  const [prontuario, setProntuario] = useState<any>(null);
  
  // Catálogos do banco de dados
  const [catalogoQueixas, setCatalogoQueixas] = useState<CatalogoItem[]>([]);
  const [catalogoSintomas, setCatalogoSintomas] = useState<CatalogoItem[]>([]);
  const [catalogoHumor, setCatalogoHumor] = useState<CatalogoItem[]>([]);
  const [catalogoMedicamentos, setCatalogoMedicamentos] = useState<Array<{id: number; nome_medicamento: string; categoria?: string}>>([]);
  
  // Estados para novos itens
  const [novaQueixa, setNovaQueixa] = useState('');
  const [novoSintoma, setNovoSintoma] = useState('');
  const [novoHumor, setNovoHumor] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Estados do prontuário
  const [queixasSelecionadas, setQueixasSelecionadas] = useState<number[]>([]);
  const [sintomasSelecionados, setSintomasSelecionados] = useState<number[]>([]);
  const [humorSelecionado, setHumorSelecionado] = useState<number[]>([]);
  const [medicamentosSelecionados, setMedicamentosSelecionados] = useState<MedicamentoSelecionado[]>([]);
  const [historicoSessao, setHistoricoSessao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [encaminhamentos, setEncaminhamentos] = useState('');
  const [planoTratamento, setPlanoTratamento] = useState('');
  const [evolucao, setEvolucao] = useState('');
  const [hipoteseDiagnostica, setHipoteseDiagnostica] = useState('');

  // Estados para controlar modal OCR
  const [showOCRModal, setShowOCRModal] = useState(false);

  // Determinar se estamos vindo de uma rota de paciente ou consulta
  const isFromPaciente = searchParams.get('source') === 'paciente';
  const isFromConsulta = searchParams.get('source') === 'consulta';

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setCarregando(true);
        setError('');
        
        // Verificar obrigatoriamente o ID
        if (!id) {
          console.error('[PRONTUARIO] ID não encontrado na URL');
          setError('ID não encontrado na URL');
          setCarregando(false);
          return;
        }
        
        // Carregar catálogos do banco de dados (em paralelo)
        const carregarCatalogos = async () => {
          try {
            console.log('[PRONTUARIO] Carregando catálogos...');
            const [queixas, sintomas, humor, medicamentos] = await Promise.all([
              prontuariosService.buscarCatalogo('queixas'),
              prontuariosService.buscarCatalogo('sintomas'),
              prontuariosService.buscarCatalogo('humor'),
              medicamentosService.obterTodos()
            ]);
            
            setCatalogoQueixas(queixas || []);
            setCatalogoSintomas(sintomas || []);
            setCatalogoHumor(humor || []);
            setCatalogoMedicamentos(medicamentos || []);
            
            console.log('[PRONTUARIO] Catálogos carregados:', { 
              queixas: queixas?.length || 0, 
              sintomas: sintomas?.length || 0, 
              humor: humor?.length || 0,
              medicamentos: medicamentos?.length || 0
            });
          } catch (catalogoError) {
            console.error('[PRONTUARIO] Erro ao carregar catálogos:', catalogoError);
            // Continue mesmo com erro nos catálogos
          }
        };

        // Inicie o carregamento dos catálogos em paralelo
        const catalogosPromise = carregarCatalogos();
        
        // CORREÇÃO PRINCIPAL: Lógica de carregamento de dados simplificada e robusta
        let consultaData: Consulta | null = null;
        let pacienteData: Paciente | null = null;
        
        // Sempre tente carregar a consulta primeiro se temos um ID
        if (id !== 'geral') {
          try {
            console.log(`[PRONTUARIO] Tentando carregar consulta com ID: ${id}`);
            consultaData = await consultasService.obterPorId(id);
            console.log('[PRONTUARIO] Consulta carregada:', consultaData);
            
            if (consultaData && consultaData.pacienteId) {
              // Se temos uma consulta válida, carregamos o paciente
              try {
                pacienteData = await pacientesService.obterPorId(consultaData.pacienteId);
                console.log('[PRONTUARIO] Paciente carregado:', pacienteData?.nome);
                console.log('[PRONTUARIO] Histórico médico do paciente:', pacienteData?.historicoMedico);
                console.log('[PRONTUARIO] Dados completos do paciente:', pacienteData);
              } catch (pacienteError) {
                console.error('[PRONTUARIO] Erro ao carregar paciente da consulta:', pacienteError);
                setError('Não foi possível carregar os dados do paciente desta consulta.');
              }
            } else {
              console.error('[PRONTUARIO] Consulta não encontrada ou sem pacienteId');
              setError('Consulta não encontrada ou inválida.');
            }
          } catch (consultaError) {
            console.error('[PRONTUARIO] Erro ao carregar consulta:', consultaError);
            setError('Não foi possível carregar os dados da consulta.');
            
            // Verificar se temos um ID de paciente no lugar de consulta
            if (isFromPaciente) {
              try {
                pacienteData = await pacientesService.obterPorId(id);
                if (pacienteData) {
                  console.log('[PRONTUARIO] Carregado paciente direto:', pacienteData.nome);
                  
                  // Criar uma "consulta virtual" para o prontuário geral
                  consultaData = {
                    id: 'geral',
                    pacienteId: id,
                    data: new Date().toISOString().split('T')[0],
                    hora: '00:00',
                    realizada: true,
                    statusConsulta: 'realizada',
                    nomePaciente: pacienteData.nome,
                  } as Consulta;
                }
              } catch (pacienteError) {
                console.error('[PRONTUARIO] Erro ao carregar paciente direto:', pacienteError);
                setError('Não foi possível carregar os dados do paciente.');
              }
            }
          }
        } else if (isFromPaciente) {
          // Caso especial para prontuário geral do paciente
          const pacienteId = searchParams.get('pacienteId');
          if (pacienteId) {
            try {
              pacienteData = await pacientesService.obterPorId(pacienteId);
              if (pacienteData) {
                // Criar uma "consulta virtual" para o prontuário geral
                consultaData = {
                  id: 'geral',
                  pacienteId,
                  data: new Date().toISOString().split('T')[0],
                  hora: '00:00',
                  realizada: true,
                  statusConsulta: 'realizada',
                  nomePaciente: pacienteData.nome,
                } as Consulta;
              }
            } catch (pacienteError) {
              console.error('[PRONTUARIO] Erro ao carregar paciente para prontuário geral:', pacienteError);
              setError('Não foi possível carregar os dados do paciente para o prontuário geral.');
            }
          } else {
            setError('ID do paciente não fornecido para o prontuário geral.');
          }
        }
        
        // Atualizar estados com os dados carregados
        setConsulta(consultaData);
        setPaciente(pacienteData);
        
        // Só tente carregar o prontuário se tivermos consulta e paciente
        if (consultaData && pacienteData) {
          try {
            const prontuarioExistente = await prontuariosService.buscarPorConsulta(consultaData.id);
            if (prontuarioExistente) {
              setProntuario(prontuarioExistente);
              
              // Preencher campos do formulário
              setHistoricoSessao(prontuarioExistente.historicoSessao || '');
              setObservacoes(prontuarioExistente.observacoes || '');
              setEncaminhamentos(prontuarioExistente.encaminhamentos || '');
              setPlanoTratamento(prontuarioExistente.planoTratamento || '');
              setEvolucao(prontuarioExistente.evolucao || '');
              setHipoteseDiagnostica(prontuarioExistente.hipoteseDiagnostica || '');
              
              // Preencher seleções seguramente
              setQueixasSelecionadas(prontuarioExistente.queixas?.map(q => q.id) || []);
              setSintomasSelecionados(prontuarioExistente.sintomas?.map(s => s.id) || []);
              setHumorSelecionado(prontuarioExistente.humor?.map(h => h.id) || []);
              
              // Preencher medicamentos salvos
              if (prontuarioExistente.medicamentos && prontuarioExistente.medicamentos.length > 0) {
                setMedicamentosSelecionados(
                  prontuarioExistente.medicamentos.map(m => ({
                    medicamento_id: m.id,
                    dosagem: m.dosagem || '',
                    posologia: m.posologia || '',
                    observacao: m.observacao || ''
                  }))
                );
              }
              
              console.log('[PRONTUARIO] Prontuário existente carregado');
            } else if (pacienteData.historicoMedico) {
              // Se não há prontuário mas temos histórico médico do paciente
              setHistoricoSessao(pacienteData.historicoMedico);
            }
          } catch (prontuarioError) {
            console.log('[PRONTUARIO] Nenhum prontuário encontrado ou erro:', prontuarioError);
            if (pacienteData.historicoMedico) {
              setHistoricoSessao(pacienteData.historicoMedico);
            }
          }
        }
        
        // Aguardar o carregamento dos catálogos concluir
        await catalogosPromise;
      } catch (error) {
        console.error('[PRONTUARIO] Erro geral ao carregar dados:', error);
        setError('Erro ao carregar dados do prontuário. Tente novamente.');
      } finally {
        setCarregando(false);
      }
    };

    carregarDados();
  }, [id, isFromPaciente, isFromConsulta, searchParams]);
  
  // Funções auxiliares e manipuladores de eventos
  const adicionarNovaQueixa = async () => {
    if (!novaQueixa.trim()) return;
    
    try {
      await catalogoService.adicionarQueixa(novaQueixa.trim());
      
      const novasQueixas = await prontuariosService.buscarCatalogo('queixas');
      setCatalogoQueixas(novasQueixas);
      
      const novaQueixaItem = novasQueixas.find(q => q.valor === novaQueixa.trim());
      if (novaQueixaItem) {
        setQueixasSelecionadas(prev => [...prev, novaQueixaItem.id]);
      }
      
      setNovaQueixa('');
      alert('Nova queixa adicionada com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar queixa:', error);
      alert('Erro ao adicionar nova queixa.');
    }
  };
  
  const adicionarNovoSintoma = async () => {
    if (!novoSintoma.trim()) return;
    
    try {
      await catalogoService.adicionarSintoma(novoSintoma.trim());
      
      const novosSintomas = await prontuariosService.buscarCatalogo('sintomas');
      setCatalogoSintomas(novosSintomas);
      
      const novoSintomaItem = novosSintomas.find(s => s.valor === novoSintoma.trim());
      if (novoSintomaItem) {
        setSintomasSelecionados(prev => [...prev, novoSintomaItem.id]);
      }
      
      setNovoSintoma('');
      alert('Novo sintoma adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar sintoma:', error);
      alert('Erro ao adicionar novo sintoma.');
    }
  };
  
  const adicionarNovoHumor = async () => {
    if (!novoHumor.trim()) return;
    
    try {
      await catalogoService.adicionarHumor(novoHumor.trim());
      
      const novosHumores = await prontuariosService.buscarCatalogo('humor');
      setCatalogoHumor(novosHumores);
      
      const novoHumorItem = novosHumores.find(h => h.valor === novoHumor.trim());
      if (novoHumorItem) {
        setHumorSelecionado(prev => [...prev, novoHumorItem.id]);
      }
      
      setNovoHumor('');
      alert('Novo humor adicionado com sucesso!');
    } catch (error) {
      console.error('Erro ao adicionar humor:', error);
      alert('Erro ao adicionar novo humor.');
    }
  };
  
  const handleToggleQueixa = (queixaId: number) => {
    setQueixasSelecionadas(prev => 
      prev.includes(queixaId)
        ? prev.filter(id => id !== queixaId)
        : [...prev, queixaId]
    );
  };
  
  const handleToggleSintoma = (sintomaId: number) => {
    setSintomasSelecionados(prev => 
      prev.includes(sintomaId)
        ? prev.filter(id => id !== sintomaId)
        : [...prev, sintomaId]
    );
  };
  
  const handleToggleHumor = (humorId: number) => {
    setHumorSelecionado(prev => 
      prev.includes(humorId)
        ? prev.filter(id => id !== humorId)
        : [...prev, humorId]
    );
  };
  
  const handleToggleMedicamento = (medicamentoId: number) => {
    setMedicamentosSelecionados(prev => {
      const jaExiste = prev.find(m => m.medicamento_id === medicamentoId);
      
      if (jaExiste) {
        // Remove medicamento
        return prev.filter(m => m.medicamento_id !== medicamentoId);
      } else {
        // Adiciona medicamento com valores padrão
        return [...prev, {
          medicamento_id: medicamentoId,
          dosagem: '',
          posologia: '',
          observacao: ''
        }];
      }
    });
  };
  
  const handleMedicamentoChange = (medicamentoId: number, campo: 'dosagem' | 'posologia' | 'observacao', valor: string) => {
    setMedicamentosSelecionados(prev => 
      prev.map(m => 
        m.medicamento_id === medicamentoId 
          ? { ...m, [campo]: valor }
          : m
      )
    );
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) {
      setError('ID da consulta não está disponível');
      return;
    }
    
    if (!consulta || !consulta.pacienteId) {
      setError('Dados da consulta não estão disponíveis');
      return;
    }

    if (!paciente) {
      setError('Dados do paciente não estão disponíveis');
      return;
    }
    
    try {
      setSalvando(true);
      
      const dadosProntuario = {
        pacienteId: parseInt(consulta.pacienteId),
        usuarioId: 1, // TODO: pegar do usuário logado
        historicoSessao,
        observacoes,
        encaminhamentos,
        planoTratamento,
        evolucao,
        hipoteseDiagnostica,
        queixas: queixasSelecionadas,
        sintomas: sintomasSelecionados,
        humor: humorSelecionado,
        medicamentos: medicamentosSelecionados
      };
      
      const consultaIdParaSalvar = consulta.id === 'geral' ? `paciente-${consulta.pacienteId}` : consulta.id;
      
      await prontuariosService.salvar(consultaIdParaSalvar, dadosProntuario);
      
      alert('Prontuário salvo com sucesso!');
      
      if (isFromPaciente) {
        navigate(`/pacientes/${consulta.pacienteId}`);
      } else {
        navigate('/consultas');
      }
    } catch (error) {
      console.error('Erro ao salvar prontuário:', error);
      alert('Ocorreu um erro ao salvar o prontuário. Por favor, tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  // NOVO: Callback quando texto for extraído do OCR
  const handleTextoOCRExtraido = (textoExtraido: string) => {
    // Adicionar texto extraído ao campo Observações
    setObservacoes((observacoesAtuais) => {
      const separador = observacoesAtuais.trim() ? '\n\n--- TEXTO EXTRAÍDO POR OCR ---\n\n' : '';
      return observacoesAtuais + separador + textoExtraido;
    });

    console.log('[PRONTUARIO] Texto OCR adicionado às observações:', {
      caracteres: textoExtraido.length
    });
  };
  
  // Renderização condicional
  if (carregando) {
    return <div className="text-center my-5"><div className="spinner-border"></div><p>Carregando dados...</p></div>;
  }
  
  if (error) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">Erro!</h4>
        <p>{error}</p>
        <hr />
        <p className="mb-0">
          <button className="btn btn-outline-danger" onClick={() => window.history.back()}>
            Voltar
          </button>
        </p>
      </div>
    );
  }
  
  if (!consulta || !paciente) {
    return (
      <DadosNaoCarregados 
        mensagem="Não foi possível carregar os dados da consulta ou do paciente." 
        tentarNovamente={() => window.location.reload()}
      />
    );
  }
  
  const formatarData = (data: string) => {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };
  
  const handleVoltar = () => {
    if (isFromPaciente && consulta?.pacienteId) {
      navigate(`/pacientes/${consulta.pacienteId}`);
    } else if (isFromPaciente && id) {
      navigate(`/pacientes/${id}`);
    } else {
      navigate('/consultas');
    }
  };
  
  return (
    <div className="container-fluid">
      <div className="card shadow mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h1 className="h4 mb-0">
            {isFromPaciente ? 'Prontuário do Paciente' : 'Prontuário da Consulta'}
          </h1>
          <button
            className="btn btn-sm btn-light"
            onClick={handleVoltar}
          >
            Voltar
          </button>
        </div>
        
        <div className="card-body">
          {/* Barra de ações fixa em mobile */}
          <div className="mobile-action-bar d-md-none mb-3">
            <button type="button" className="btn btn-secondary" onClick={handleVoltar} disabled={salvando}>Cancelar</button>
            <button type="submit" form="form-prontuario" className="btn btn-primary" disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
          {/* Informações sobre o paciente e consulta */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card bg-light">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h5 className="mb-3">Dados do Paciente</h5>
                      <p><strong>Nome:</strong> {paciente?.nome || 'N/A'}</p>
                      <p><strong>E-mail:</strong> {paciente?.email || 'N/A'}</p> {/* ← Safe access com || */}
                      <p><strong>Telefone:</strong> {paciente?.telefone || 'N/A'}</p>
                    </div>
                    <div className="col-md-6">
                      <h5 className="mb-3">Dados da Consulta</h5>
                      <p><strong>Data:</strong> {consulta?.data ? formatarData(consulta.data) : 'N/A'}</p>
                      <p><strong>Hora:</strong> {consulta?.hora || 'N/A'}</p>
                      <p>
                        <strong>Status:</strong> 
                        <span className={`badge ${consulta?.realizada ? 'bg-success' : 'bg-warning'} ms-2`}>
                          {consulta?.realizada ? 'Realizada' : 'Pendente'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Formulário do prontuário */}
          <form id="form-prontuario" onSubmit={handleSubmit}>
            {/* Seção: Histórico Médico do Paciente */}
            {/* Histórico Médico do Paciente - SEMPRE MOSTRAR */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card border-info">
                  <div className="card-header bg-info text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-notes-medical mr-2"></i>
                      Histórico Médico do Paciente
                    </h5>
                  </div>
                  <div className="card-body">
                    {paciente?.historicoMedico ? (
                      <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{paciente.historicoMedico}</p>
                    ) : (
                      <p className="text-muted mb-0"><em>Nenhum histórico médico registrado.</em></p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Seção: Problemas Clínicos do Paciente - SEMPRE MOSTRAR */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card border-warning">
                  <div className="card-header bg-warning text-dark">
                    <h5 className="mb-0">
                      <i className="fas fa-clipboard-list mr-2"></i>
                      Problemas Clínicos do Paciente
                    </h5>
                  </div>
                  <div className="card-body">
                    {paciente?.problemasClinicos ? (
                      <div className="row">
                        {(() => {
                          // Suportar ambos formatos: array ou Record<string, boolean>
                          const problemas = Array.isArray(paciente.problemasClinicos)
                            ? paciente.problemasClinicos
                            : Object.keys(paciente.problemasClinicos || {}).filter(
                                key => paciente.problemasClinicos?.[key]
                              );
                          
                          if (problemas.length === 0) {
                            return (
                              <div className="col-12">
                                <p className="text-muted mb-0"><em>Nenhum problema clínico registrado.</em></p>
                              </div>
                            );
                          }
                          
                          return problemas.map((problema: any, index: number) => (
                            <div className="col-md-6 mb-2" key={index}>
                              <div className="alert alert-warning mb-0 py-2">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                <strong>
                                  {typeof problema === 'string' 
                                    ? problema 
                                    : problema.problema_clinico || problema.valor || 'Não especificado'}
                                </strong>
                                {problema.data_diagnostico && (
                                  <small className="d-block text-muted mt-1">
                                    Diagnóstico: {formatarData(problema.data_diagnostico)}
                                  </small>
                                )}
                                {problema.observacoes && (
                                  <small className="d-block mt-1">{problema.observacoes}</small>
                                )}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : (
                      <p className="text-muted mb-0"><em>Nenhum problema clínico registrado.</em></p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Seção para Queixas */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="form-label fw-bold">💬 Queixas</div>
                <div className="card p-3">
                  {/* Dropdown para adicionar queixa */}
                  <div className="mb-3">
                    <select 
                      className="form-select"
                      onChange={(e) => {
                        const queixaId = parseInt(e.target.value);
                        if (queixaId) {
                          handleToggleQueixa(queixaId);
                          e.target.value = '';
                        }
                      }}
                      value=""
                    >
                      <option value="">+ Adicionar queixa...</option>
                      {catalogoQueixas
                        .filter(q => !queixasSelecionadas.includes(q.id))
                        .map(queixa => (
                          <option key={queixa.id} value={queixa.id}>
                            {queixa.valor}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  {/* Lista de queixas selecionadas */}
                  {queixasSelecionadas.length > 0 && (
                    <div className="queixas-lista">
                      <div className="row g-2">
                        {queixasSelecionadas.map((queixaId) => {
                          const queixa = catalogoQueixas.find(q => q.id === queixaId);
                          if (!queixa) return null;

                          return (
                            <div key={queixa.id} className="col-md-6">
                              <div className="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                                <span>{queixa.valor}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleToggleQueixa(queixa.id)}
                                  title="Remover queixa"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {queixasSelecionadas.length === 0 && (
                    <p className="text-muted text-center mb-0">
                      <i className="fas fa-info-circle me-1"></i>
                      Nenhuma queixa selecionada. Use o campo acima para adicionar.
                    </p>
                  )}

                  {/* Input para adicionar nova queixa ao catálogo */}
                  <div className="input-group mt-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Criar nova queixa no catálogo..."
                      value={novaQueixa}
                      onChange={(e) => setNovaQueixa(e.target.value)}
                    />
                    <button 
                      className="btn btn-outline-primary" 
                      type="button"
                      onClick={adicionarNovaQueixa}
                    >
                      <i className="fas fa-plus me-1"></i> Criar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção para Sintomas */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="form-label fw-bold">🩺 Sintomas</div>
                <div className="card p-3">
                  {/* Dropdown para adicionar sintoma */}
                  <div className="mb-3">
                    <select 
                      className="form-select"
                      onChange={(e) => {
                        const sintomaId = parseInt(e.target.value);
                        if (sintomaId) {
                          handleToggleSintoma(sintomaId);
                          e.target.value = '';
                        }
                      }}
                      value=""
                    >
                      <option value="">+ Adicionar sintoma...</option>
                      {catalogoSintomas
                        .filter(s => !sintomasSelecionados.includes(s.id))
                        .map(sintoma => (
                          <option key={sintoma.id} value={sintoma.id}>
                            {sintoma.valor}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  {/* Lista de sintomas selecionados */}
                  {sintomasSelecionados.length > 0 && (
                    <div className="sintomas-lista">
                      <div className="row g-2">
                        {sintomasSelecionados.map((sintomaId) => {
                          const sintoma = catalogoSintomas.find(s => s.id === sintomaId);
                          if (!sintoma) return null;

                          return (
                            <div key={sintoma.id} className="col-md-6">
                              <div className="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                                <span>{sintoma.valor}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleToggleSintoma(sintoma.id)}
                                  title="Remover sintoma"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {sintomasSelecionados.length === 0 && (
                    <p className="text-muted text-center mb-0">
                      <i className="fas fa-info-circle me-1"></i>
                      Nenhum sintoma selecionado. Use o campo acima para adicionar.
                    </p>
                  )}

                  {/* Input para adicionar novo sintoma ao catálogo */}
                  <div className="input-group mt-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Criar novo sintoma no catálogo..."
                      value={novoSintoma}
                      onChange={(e) => setNovoSintoma(e.target.value)}
                    />
                    <button 
                      className="btn btn-outline-primary" 
                      type="button"
                      onClick={adicionarNovoSintoma}
                    >
                      <i className="fas fa-plus me-1"></i> Criar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Seção para Humor */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="form-label fw-bold">😊 Humor do Paciente</div>
                <div className="card p-3">
                  {/* Dropdown para adicionar humor */}
                  <div className="mb-3">
                    <select 
                      className="form-select"
                      onChange={(e) => {
                        const humorId = parseInt(e.target.value);
                        if (humorId) {
                          handleToggleHumor(humorId);
                          e.target.value = '';
                        }
                      }}
                      value=""
                    >
                      <option value="">+ Adicionar estado de humor...</option>
                      {catalogoHumor
                        .filter(h => !humorSelecionado.includes(h.id))
                        .map(humor => (
                          <option key={humor.id} value={humor.id}>
                            {humor.valor}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  {/* Lista de humores selecionados */}
                  {humorSelecionado.length > 0 && (
                    <div className="humor-lista">
                      <div className="row g-2">
                        {humorSelecionado.map((humorId) => {
                          const humor = catalogoHumor.find(h => h.id === humorId);
                          if (!humor) return null;

                          return (
                            <div key={humor.id} className="col-md-4">
                              <div className="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                                <span>{humor.valor}</span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleToggleHumor(humor.id)}
                                  title="Remover humor"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {humorSelecionado.length === 0 && (
                    <p className="text-muted text-center mb-0">
                      <i className="fas fa-info-circle me-1"></i>
                      Nenhum humor selecionado. Use o campo acima para adicionar.
                    </p>
                  )}

                  {/* Input para adicionar novo humor ao catálogo */}
                  <div className="input-group mt-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Criar novo humor no catálogo..."
                      value={novoHumor}
                      onChange={(e) => setNovoHumor(e.target.value)}
                    />
                    <button 
                      className="btn btn-outline-primary" 
                      type="button"
                      onClick={adicionarNovoHumor}
                    >
                      <i className="fas fa-plus me-1"></i> Criar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Seção para Medicamentos em Uso */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="form-label fw-bold">💊 Medicamentos em Uso</div>
                <div className="card p-3">
                  {/* Dropdown para adicionar medicamento */}
                  <div className="mb-3">
                    <select 
                      className="form-select"
                      onChange={(e) => {
                        const medicamentoId = parseInt(e.target.value);
                        if (medicamentoId) {
                          handleToggleMedicamento(medicamentoId);
                          e.target.value = ''; // Resetar dropdown
                        }
                      }}
                      value=""
                    >
                      <option value="">+ Adicionar medicamento...</option>
                      {catalogoMedicamentos
                        .filter(med => !medicamentosSelecionados.find(m => m.medicamento_id === med.id))
                        .map(medicamento => (
                          <option key={medicamento.id} value={medicamento.id}>
                            {medicamento.nome_medicamento}
                            {medicamento.categoria ? ` (${medicamento.categoria})` : ''}
                          </option>
                        ))
                      }
                    </select>
                    {catalogoMedicamentos.length === 0 && (
                      <small className="text-muted">Nenhum medicamento cadastrado. Adicione em Configurações → Personalizar.</small>
                    )}
                  </div>

                  {/* Lista de medicamentos selecionados */}
                  {medicamentosSelecionados.length > 0 && (
                    <div className="medicamentos-lista">
                      {medicamentosSelecionados.map((medicamentoSel) => {
                        const medicamento = catalogoMedicamentos.find(m => m.id === medicamentoSel.medicamento_id);
                        if (!medicamento) return null;

                        return (
                          <div key={medicamento.id} className="border rounded p-3 mb-3 bg-light">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <strong>{medicamento.nome_medicamento}</strong>
                                {medicamento.categoria && (
                                  <span className="badge bg-success ms-2">{medicamento.categoria}</span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleToggleMedicamento(medicamento.id)}
                                title="Remover medicamento"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                            
                            <div className="row g-2">
                              <div className="col-md-3">
                                <label className="form-label small mb-1" htmlFor={`medicamentoDosagem-${medicamento.id}`}>Dosagem</label>
                                <input
                                  id={`medicamentoDosagem-${medicamento.id}`}
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Ex: 20mg, 0.5mg"
                                  value={medicamentoSel.dosagem}
                                  onChange={(e) => handleMedicamentoChange(medicamento.id, 'dosagem', e.target.value)}
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small mb-1" htmlFor={`medicamentoPosologia-${medicamento.id}`}>Posologia</label>
                                <select
                                  id={`medicamentoPosologia-${medicamento.id}`}
                                  className="form-select form-select-sm"
                                  value={medicamentoSel.posologia}
                                  onChange={(e) => handleMedicamentoChange(medicamento.id, 'posologia', e.target.value)}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="1x ao dia">1x ao dia</option>
                                  <option value="2x ao dia">2x ao dia</option>
                                  <option value="3x ao dia">3x ao dia</option>
                                  <option value="1x ao dia (manhã)">1x ao dia (manhã)</option>
                                  <option value="1x ao dia (noite)">1x ao dia (noite)</option>
                                  <option value="2x ao dia (manhã e noite)">2x ao dia (manhã e noite)</option>
                                  <option value="A cada 8 horas">A cada 8 horas</option>
                                  <option value="A cada 12 horas">A cada 12 horas</option>
                                  <option value="SOS (se necessário)">SOS (se necessário)</option>
                                  <option value="Conforme orientação médica">Conforme orientação médica</option>
                                </select>
                              </div>
                              <div className="col-md-5">
                                <label className="form-label small mb-1" htmlFor={`medicamentoObservacao-${medicamento.id}`}>Observações</label>
                                <input
                                  id={`medicamentoObservacao-${medicamento.id}`}
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Observações sobre o uso..."
                                  value={medicamentoSel.observacao}
                                  onChange={(e) => handleMedicamentoChange(medicamento.id, 'observacao', e.target.value)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {medicamentosSelecionados.length === 0 && (
                    <p className="text-muted text-center mb-0 mt-2">
                      <i className="fas fa-info-circle me-1"></i>
                      Nenhum medicamento selecionado. Use o campo acima para adicionar.
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="historicoSessao" className="form-label fw-bold">Histórico da Sessão</label>
              <textarea
                className="form-control"
                id="historicoSessao"
                rows={4}
                value={historicoSessao}
                onChange={(e) => setHistoricoSessao(e.target.value)}
              ></textarea>
            </div>

            <div className="mb-3">
              <label htmlFor="observacoes" className="form-label fw-bold">
                📄 Observações da Consulta
              </label>
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowOCRModal(true)}
                  disabled={salvando}
                >
                  <i className="fas fa-file-image me-2"></i>
                  Digitalizar Documento (OCR)
                </button>
                <small className="text-muted ms-2 d-block mt-1">
                  Extrair texto de documentos manuscritos ou digitalizados
                </small>
              </div>
              <textarea
                className="form-control"
                id="observacoes"
                rows={6}
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                disabled={salvando}
                placeholder="Observações gerais sobre a consulta... (ou use o botão acima para extrair texto de um documento)"
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="encaminhamentos" className="form-label fw-bold">Encaminhamentos</label>
              <textarea
                className="form-control"
                id="encaminhamentos"
                rows={3}
                value={encaminhamentos}
                onChange={(e) => setEncaminhamentos(e.target.value)}
                disabled={salvando}
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="planoTratamento" className="form-label fw-bold">Plano de Tratamento</label>
              <textarea
                className="form-control"
                id="planoTratamento"
                rows={3}
                value={planoTratamento}
                onChange={(e) => setPlanoTratamento(e.target.value)}
                disabled={salvando}
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="evolucao" className="form-label fw-bold">Evolução do Paciente</label>
              <textarea
                className="form-control"
                id="evolucao"
                rows={3}
                value={evolucao}
                onChange={(e) => setEvolucao(e.target.value)}
                disabled={salvando}
                placeholder="Descreva a evolução do paciente ao longo do tratamento..."
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="hipoteseDiagnostica" className="form-label fw-bold">Hipótese Diagnóstica</label>
              <textarea
                className="form-control"
                id="hipoteseDiagnostica"
                rows={3}
                value={hipoteseDiagnostica}
                onChange={(e) => setHipoteseDiagnostica(e.target.value)}
                disabled={salvando}
                placeholder="Registre a hipótese diagnóstica baseada nas observações clínicas..."
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleVoltar}
                disabled={salvando}
              >
                Cancelar
              </button>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={salvando}
              >
                {salvando ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : 'Salvar Prontuário'}
              </button>
            </div>
          </form>

          {/* Modal OCR */}
          <OCRUploadModal
            show={showOCRModal}
            onClose={() => setShowOCRModal(false)}
            onTextoExtraido={handleTextoOCRExtraido}
            pacienteId={paciente?.id ? (typeof paciente.id === 'string' ? parseInt(paciente.id) : paciente.id) : 0}
            pacienteNome={paciente?.nome}
          />
        </div>
      </div>
    </div>
  );
}